import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, CheckCircle2, ClipboardList, ExternalLink, GraduationCap, ListChecks, Target, type LucideIcon } from "lucide-react";

import { curriculumService } from "../services/curriculumService";
import { useAuthStore } from "../stores/authStore";
import { CoveragePanel, RecordsPanel } from "../components/coverage/CoveragePanels";

type Tab = "coverage" | "records";

/**
 * Curriculum coverage: how far each class has got through each syllabus, from
 * what teachers record as they teach. The structure it covers is kept
 * elsewhere: curricula and subjects in Academics, syllabi and topics in Syllabi.
 */
export default function Curriculum() {
  const me = useAuthStore((state) => state.user?.id);
  const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
    { id: "coverage", label: "Coverage by class", icon: Target },
    { id: "records", label: "Topic records", icon: ListChecks },
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "records" ? "records" : "coverage";
  const view = params.get("view") ?? undefined;
  const select = (next: Tab, nextView?: string) => {
    const query: Record<string, string> = {};
    if (next !== "coverage") query.tab = next;
    if (nextView) query.view = nextView;
    setParams(query, { replace: true });
  };

  const queryClient = useQueryClient();
  const coverage = useQuery({ queryKey: ["coverage", "overview"], queryFn: curriculumService.getCoverage });
  const records = useQuery({ queryKey: ["coverage", "all-records"], queryFn: curriculumService.getProgress });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["coverage"] });

  const rows = coverage.data ?? [];
  const mine = rows.filter((row) => row.can_record);
  const scope = mine.length ? mine : rows;
  const topics = scope.reduce((sum, row) => sum + row.topic_count, 0);
  const done = scope.reduce((sum, row) => sum + row.completed, 0);
  const underWay = scope.reduce((sum, row) => sum + row.in_progress, 0);
  const behind = scope.reduce((sum, row) => sum + row.behind, 0);
  const unstarted = scope.filter((row) => row.topic_count > 0 && !row.completed && !row.in_progress).length;
  const dash = (text: string) => (coverage.isLoading ? "–" : text);
  const who = mine.length ? "Your classes" : "Across the school";

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: `${who}: covered`, value: dash(topics ? `${((done / topics) * 100).toFixed(1)}%` : "—"), note: `${done} of ${topics} topics done`, go: () => select("coverage", mine.length ? "mine" : undefined), icon: CheckCircle2 },
    { label: "Topics under way", value: dash(String(underWay)), note: behind ? `${behind} past their planned weeks` : "None behind", go: () => select("records", "open"), icon: ClipboardList },
    { label: "Behind", value: dash(String(behind)), note: behind ? "Topics running over" : "Nothing running over", go: () => select("coverage", "behind"), icon: AlertTriangle, alert: behind > 0 },
    { label: "Not started", value: dash(String(unstarted)), note: unstarted === 1 ? "class syllabus with no topic begun" : "class syllabi with no topic begun", go: () => select("coverage", "unstarted"), icon: Target, alert: unstarted > 0 && mine.length > 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Curriculum coverage</h1>
          <p className="mt-1 text-sm text-gray-500">How far each class has got through each syllabus, recorded by teachers as they teach. Start a topic, update how much is covered, and mark it done.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/syllabi" className="btn btn-outline h-9 gap-1.5 text-sm"><BookOpen className="h-4 w-4" /> Syllabi and topics <ExternalLink className="h-3.5 w-3.5" /></Link>
          <Link to="/academics?tab=subjects" className="btn btn-outline h-9 gap-1.5 text-sm"><GraduationCap className="h-4 w-4" /> Curricula and subjects <ExternalLink className="h-3.5 w-3.5" /></Link>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Coverage sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = item.id === "coverage" ? behind : 0;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "coverage" && <CoveragePanel rows={coverage.data} me={me} isLoading={coverage.isLoading} isError={coverage.isError} onRetry={() => coverage.refetch()} onChanged={refresh} initialView={view ?? (mine.length ? "mine" : undefined)} />}
      {tab === "records" && <RecordsPanel records={records.data} me={me} isLoading={records.isLoading} isError={records.isError} onRetry={() => records.refetch()} onChanged={refresh} />}
    </div>
  );
}
